import React, { useEffect, useState, useCallback } from "react";
import Layout from "../components/Layout/Layout";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setUser } from "../redux/features/userSlice";
import axios from "axios";
import { message } from "antd";
import { GoogleLogin } from "@react-oauth/google";
import OTPInput from "otp-input-react";
import "./Login.css";
import logo from "../img/logo.png";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

function EyeIcon({ open }) {
  return open ? (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  ) : (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94" />
      <path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );
}

const Login = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [tab, setTab] = useState("google");
  const [loading, setLoad] = useState(false);

  const [form, setForm] = useState({
    fname: "",
    email: "",
    password: "",
    confirm: "",
  });
  const [showPass, setShowPass] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const [otp, setOtp] = useState("");
  const [otpEmail, setOtpEmail] = useState("");
  const [otpResent, setOtpResent] = useState(false);
  const [countdown, setCountdown] = useState(0);

  /* forgot / reset password */
  const [resetEmail, setResetEmail] = useState("");
  const [resetOtp, setResetOtp] = useState("");
  const [newPass, setNewPass] = useState("");
  const [newPassConfirm, setNewPassConfirm] = useState("");
  const [showNewPass, setShowNewPass] = useState(false);

  const [btnWidth, setBtnWidth] = useState(280);
  useEffect(() => {
    const calc = () => setBtnWidth(Math.max(220, Math.min(window.innerWidth - 48, 360)));
    calc();
    window.addEventListener("resize", calc);
    return () => window.removeEventListener("resize", calc);
  }, []);

  useEffect(() => {
    if (countdown <= 0) return;
    const t = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [countdown]);

  const handleField = (e) => setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const saveToken = useCallback((data) => {
    localStorage.setItem("token", data.token);
    if (data.user) dispatch(setUser(data.user));
    navigate(data.isAdmin ? "/admin-dashboard" : "/");
  }, [navigate, dispatch]);

  const authCopy = {
    google: {
      pill: "Quick access",
      title: "Welcome to LIMPOOK",
      text: "Sign in securely and manage top-ups, wallet reloads, orders, and support from one place.",
    },
    login: {
      pill: "Email login",
      title: "Welcome back",
      text: "Enter your email and password to continue to your account.",
    },
    register: {
      pill: "New account",
      title: "Create your account",
      text: "Join once and keep your game top-ups, orders, and wallet history together.",
    },
    otp: {
      pill: "Verification",
      title: "Verify your email",
      text: "Enter the one-time code we sent to finish securing your account.",
    },
    forgot: {
      pill: "Password reset",
      title: "Forgot your password?",
      text: "Enter your email and we'll send a 6-digit reset code.",
    },
    reset: {
      pill: "New password",
      title: "Set new password",
      text: "Enter the reset code from your email and choose a new password.",
    },
  }[tab] || { pill: "", title: "", text: "" };

  const handleGoogleSuccess = async (cred) => {
    try {
      const id_token = cred?.credential;
      if (!id_token) return message.error("Google login failed");
      setLoad(true);
      const res = await axios.post("/api/user/google-login", { id_token });
      if (res.data?.success) {
        saveToken(res.data);
      } else {
        message.error(res.data?.message || "Login failed");
      }
    } catch {
      message.error("Google login error");
    } finally {
      setLoad(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    const { fname, email, password, confirm } = form;

    if (!fname.trim()) return message.error("Full name is required");
    if (!EMAIL_RE.test(email)) return message.error("Enter a valid email address");
    if (password.length < 8) return message.error("Password must be at least 8 characters");
    if (password !== confirm) return message.error("Passwords do not match");

    setLoad(true);
    try {
      const res = await axios.post("/api/user/register", { fname: fname.trim(), email, password });
      if (res.data?.success) {
        setOtpEmail(email);
        setOtp("");
        setTab("otp");
        setCountdown(60);
        message.success("OTP sent to your email!");
      } else {
        message.error(res.data?.message || "Registration failed");
      }
    } catch (err) {
      message.error(err.response?.data?.message || "Registration failed");
    } finally {
      setLoad(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (otp.length !== 6) return message.error("Enter the 6-digit OTP");
    setLoad(true);
    try {
      const res = await axios.post("/api/user/verify-otp", { email: otpEmail, otp });
      if (res.data?.success) {
        message.success(res.data.message || "Email verified!");
        saveToken(res.data);
      } else {
        message.error(res.data?.message || "Invalid OTP");
      }
    } catch (err) {
      message.error(err.response?.data?.message || "Verification failed");
    } finally {
      setLoad(false);
    }
  };

  const handleResend = async () => {
    if (countdown > 0) return;
    setLoad(true);
    try {
      const res = await axios.post("/api/user/resend-otp", { email: otpEmail });
      if (res.data?.success) {
        message.success("OTP resent!");
        setOtpResent(true);
        setCountdown(60);
        setOtp("");
      } else {
        message.error(res.data?.message || "Failed to resend OTP");
      }
    } catch (err) {
      message.error(err.response?.data?.message || "Failed to resend OTP");
    } finally {
      setLoad(false);
    }
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    const { email, password } = form;
    if (!EMAIL_RE.test(email)) return message.error("Enter a valid email address");
    if (!password) return message.error("Password is required");

    setLoad(true);
    try {
      const res = await axios.post("/api/user/email-login", { email, password });
      if (res.data?.success) {
        saveToken(res.data);
      } else if (res.data?.needsVerification) {
        setOtpEmail(res.data.email || email);
        setOtp("");
        setTab("otp");
        setCountdown(60);
        message.warning("Email not verified. Enter the OTP sent to you.");
      } else {
        message.error(res.data?.message || "Login failed");
      }
    } catch (err) {
      const d = err.response?.data;
      if (d?.needsVerification) {
        setOtpEmail(d.email || email);
        setOtp("");
        setTab("otp");
        setCountdown(60);
        message.warning("Email not verified. Enter the OTP sent to you.");
      } else {
        message.error(d?.message || "Login failed");
      }
    } finally {
      setLoad(false);
    }
  };

  /* ── Forgot password — send code ── */
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!EMAIL_RE.test(resetEmail)) return message.error("Enter a valid email address");
    setLoad(true);
    try {
      const res = await axios.post("/api/user/forgot-password", { email: resetEmail });
      if (res.data?.success) {
        message.success(res.data.message);
        setResetOtp("");
        setNewPass("");
        setNewPassConfirm("");
        setTab("reset");
        setCountdown(60);
      } else {
        message.error(res.data?.message || "Failed to send reset code");
      }
    } catch (err) {
      message.error(err.response?.data?.message || "Failed to send reset code");
    } finally {
      setLoad(false);
    }
  };

  /* ── Reset password — verify code + set new password ── */
  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (resetOtp.length !== 6) return message.error("Enter the 6-digit reset code");
    if (newPass.length < 8)    return message.error("Password must be at least 8 characters");
    if (newPass !== newPassConfirm) return message.error("Passwords do not match");
    setLoad(true);
    try {
      const res = await axios.post("/api/user/reset-password", {
        email: resetEmail, otp: resetOtp, password: newPass,
      });
      if (res.data?.success) {
        message.success(res.data.message || "Password reset!");
        saveToken(res.data);
      } else {
        message.error(res.data?.message || "Reset failed");
      }
    } catch (err) {
      message.error(err.response?.data?.message || "Reset failed");
    } finally {
      setLoad(false);
    }
  };

  /* ── Resend reset code ── */
  const handleResendReset = async () => {
    if (countdown > 0) return;
    setLoad(true);
    try {
      const res = await axios.post("/api/user/forgot-password", { email: resetEmail });
      if (res.data?.success) {
        message.success("Reset code resent!");
        setResetOtp("");
        setCountdown(60);
      } else {
        message.error(res.data?.message || "Failed to resend");
      }
    } catch (err) {
      message.error(err.response?.data?.message || "Failed to resend");
    } finally {
      setLoad(false);
    }
  };

  return (
    <Layout>
      <div className="auth-bg">
        <div className="auth-shell">
          <aside className="auth-brand-panel" aria-label="LIMPOOK account benefits">
            <div className="auth-logo-lockup">
              <img src={logo} alt="LIMPOOK" />
              <div>
                <span>LIMPOOK</span>
                <small>Official Store</small>
              </div>
            </div>

            <div className="auth-brand-copy">
              <span className="auth-kicker">Secure account access</span>
              <h1>Login and keep your game top-ups moving.</h1>
              <p>
                Wallet reloads, order tracking, region checks, and support stay ready
                whenever you need them.
              </p>
            </div>

            <div className="auth-benefits">
              <span>Fast checkout</span>
              <span>Order updates</span>
              <span>Secure wallet</span>
            </div>
          </aside>

          <div className="auth-card">
            <div className="auth-card-head">
              <div className="auth-card-logo">
                <img src={logo} alt="LIMPOOK" />
              </div>
              <span className="auth-card-pill">{authCopy.pill}</span>
              <h2>{authCopy.title}</h2>
              <p>{authCopy.text}</p>
            </div>

            {tab === "forgot" ? (
              <form className="auth-form" onSubmit={handleForgotPassword} autoComplete="off">
                <div className="auth-field">
                  <label className="auth-label">Email address</label>
                  <input
                    className="auth-input"
                    type="email"
                    placeholder="you@example.com"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    disabled={loading}
                    autoFocus
                  />
                </div>

                <button className="auth-btn" type="submit" disabled={loading}>
                  {loading ? <span className="auth-spinner" /> : "Send Reset Code"}
                </button>

                <p className="auth-switch">
                  <button type="button" className="auth-link" onClick={() => setTab("login")}>
                    Back to Sign In
                  </button>
                </p>
              </form>

            ) : tab === "reset" ? (
              <form className="auth-form" onSubmit={handleResetPassword} autoComplete="off">
                <p className="auth-sub">
                  Code sent to <b style={{ color: "#ffd455" }}>{resetEmail}</b>
                </p>

                <div className="auth-field">
                  <label className="auth-label">6-digit reset code</label>
                  <div className="auth-otp-input-wrap" style={{ justifyContent: "flex-start" }}>
                    <OTPInput
                      value={resetOtp}
                      onChange={setResetOtp}
                      autoFocus
                      OTPLength={6}
                      otpType="number"
                      disabled={loading}
                      inputClassName="auth-otp-box"
                    />
                  </div>
                </div>

                <div className="auth-field">
                  <label className="auth-label">New password <span className="auth-hint">(min 8 chars)</span></label>
                  <div className="auth-pass-wrap">
                    <input
                      className="auth-input"
                      type={showNewPass ? "text" : "password"}
                      placeholder="New password"
                      value={newPass}
                      onChange={(e) => setNewPass(e.target.value)}
                      disabled={loading}
                    />
                    <button type="button" className="auth-eye" onClick={() => setShowNewPass((p) => !p)} tabIndex={-1}>
                      <EyeIcon open={showNewPass} />
                    </button>
                  </div>
                </div>

                <div className="auth-field">
                  <label className="auth-label">Confirm new password</label>
                  <div className="auth-pass-wrap">
                    <input
                      className="auth-input"
                      type={showNewPass ? "text" : "password"}
                      placeholder="Confirm password"
                      value={newPassConfirm}
                      onChange={(e) => setNewPassConfirm(e.target.value)}
                      disabled={loading}
                    />
                  </div>
                </div>

                <button className="auth-btn" type="submit" disabled={loading || resetOtp.length !== 6}>
                  {loading ? <span className="auth-spinner" /> : "Reset Password"}
                </button>

                <button
                  type="button"
                  className="auth-link-btn"
                  onClick={handleResendReset}
                  disabled={loading || countdown > 0}
                >
                  {countdown > 0 ? `Resend code in ${countdown}s` : "Resend code"}
                </button>

                <p className="auth-switch" style={{ marginTop: 8 }}>
                  <button type="button" className="auth-link" onClick={() => setTab("forgot")}>
                    Change email
                  </button>
                </p>
              </form>

            ) : tab === "otp" ? (
              <div className="auth-otp-wrap">
                <p className="auth-sub">
                  We sent a 6-digit OTP to<br />
                  <span className="auth-otp-email">{otpEmail}</span>
                </p>

                <div className="auth-otp-input-wrap">
                  <OTPInput
                    value={otp}
                    onChange={setOtp}
                    autoFocus
                    OTPLength={6}
                    otpType="number"
                    disabled={loading}
                    inputClassName="auth-otp-box"
                  />
                </div>

                {otpResent && (
                  <p className="auth-otp-resent">OTP resent! Check your inbox.</p>
                )}

                <button
                  type="button"
                  className="auth-btn"
                  onClick={handleVerifyOtp}
                  disabled={loading || otp.length !== 6}
                >
                  {loading ? <span className="auth-spinner" /> : "Verify OTP"}
                </button>

                <button
                  type="button"
                  className="auth-link-btn"
                  onClick={handleResend}
                  disabled={loading || countdown > 0}
                >
                  {countdown > 0 ? `Resend OTP in ${countdown}s` : "Resend OTP"}
                </button>

                <button
                  type="button"
                  className="auth-back-btn"
                  onClick={() => { setTab("register"); setOtp(""); }}
                >
                  Back to Register
                </button>
              </div>
            ) : (
              <>
                <div className="auth-tabs">
                  <button
                    type="button"
                    className={`auth-tab${tab === "google" ? " active" : ""}`}
                    onClick={() => setTab("google")}
                  >
                    Google
                  </button>
                  <button
                    type="button"
                    className={`auth-tab${tab === "login" ? " active" : ""}`}
                    onClick={() => setTab("login")}
                  >
                    Sign In
                  </button>
                  <button
                    type="button"
                    className={`auth-tab${tab === "register" ? " active" : ""}`}
                    onClick={() => setTab("register")}
                  >
                    Register
                  </button>
                </div>

                {tab === "google" && (
                  <div className="auth-google-wrap">
                    <p className="auth-sub">Sign in with your Google account.</p>
                    <div className="auth-google-btn-wrap">
                      <GoogleLogin
                        onSuccess={handleGoogleSuccess}
                        onError={() => message.error("Google sign-in failed")}
                        shape="pill"
                        theme="outline"
                        width={btnWidth}
                      />
                    </div>
                    <p className="auth-note">
                      First login automatically creates your account.
                    </p>
                  </div>
                )}

                {tab === "login" && (
                  <form className="auth-form" onSubmit={handleEmailLogin} autoComplete="on">
                    <p className="auth-sub">Sign in with email and password.</p>

                    <div className="auth-field">
                      <label className="auth-label">Email</label>
                      <input
                        className="auth-input"
                        type="email"
                        name="email"
                        autoComplete="email"
                        placeholder="you@example.com"
                        value={form.email}
                        onChange={handleField}
                        disabled={loading}
                      />
                    </div>

                    <div className="auth-field">
                      <label className="auth-label">Password</label>
                      <div className="auth-pass-wrap">
                        <input
                          className="auth-input"
                          type={showPass ? "text" : "password"}
                          name="password"
                          autoComplete="current-password"
                          placeholder="Password"
                          value={form.password}
                          onChange={handleField}
                          disabled={loading}
                        />
                        <button
                          type="button"
                          className="auth-eye"
                          onClick={() => setShowPass((p) => !p)}
                          tabIndex={-1}
                        >
                          <EyeIcon open={showPass} />
                        </button>
                      </div>
                    </div>

                    <button className="auth-btn" type="submit" disabled={loading}>
                      {loading ? <span className="auth-spinner" /> : "Sign In"}
                    </button>

                    <p className="auth-switch">
                      <button
                        type="button"
                        className="auth-link"
                        onClick={() => { setResetEmail(form.email || ""); setTab("forgot"); }}
                      >
                        Forgot password?
                      </button>
                    </p>

                    <p className="auth-switch">
                      No account?{" "}
                      <button type="button" className="auth-link" onClick={() => setTab("register")}>
                        Create one
                      </button>
                    </p>
                  </form>
                )}

                {tab === "register" && (
                  <form className="auth-form" onSubmit={handleRegister} autoComplete="off">
                    <p className="auth-sub">Create a new account.</p>

                    <div className="auth-field">
                      <label className="auth-label">Full Name</label>
                      <input
                        className="auth-input"
                        type="text"
                        name="fname"
                        autoComplete="name"
                        placeholder="Your full name"
                        value={form.fname}
                        onChange={handleField}
                        disabled={loading}
                        maxLength={80}
                      />
                    </div>

                    <div className="auth-field">
                      <label className="auth-label">Email</label>
                      <input
                        className="auth-input"
                        type="email"
                        name="email"
                        autoComplete="email"
                        placeholder="you@example.com"
                        value={form.email}
                        onChange={handleField}
                        disabled={loading}
                      />
                    </div>

                    <div className="auth-field">
                      <label className="auth-label">Password <span className="auth-hint">(min 8 chars)</span></label>
                      <div className="auth-pass-wrap">
                        <input
                          className="auth-input"
                          type={showPass ? "text" : "password"}
                          name="password"
                          autoComplete="new-password"
                          placeholder="Password"
                          value={form.password}
                          onChange={handleField}
                          disabled={loading}
                        />
                        <button
                          type="button"
                          className="auth-eye"
                          onClick={() => setShowPass((p) => !p)}
                          tabIndex={-1}
                        >
                          <EyeIcon open={showPass} />
                        </button>
                      </div>
                    </div>

                    <div className="auth-field">
                      <label className="auth-label">Confirm Password</label>
                      <div className="auth-pass-wrap">
                        <input
                          className="auth-input"
                          type={showConfirm ? "text" : "password"}
                          name="confirm"
                          autoComplete="new-password"
                          placeholder="Confirm password"
                          value={form.confirm}
                          onChange={handleField}
                          disabled={loading}
                        />
                        <button
                          type="button"
                          className="auth-eye"
                          onClick={() => setShowConfirm((p) => !p)}
                          tabIndex={-1}
                        >
                          <EyeIcon open={showConfirm} />
                        </button>
                      </div>
                    </div>

                    <button className="auth-btn" type="submit" disabled={loading}>
                      {loading ? <span className="auth-spinner" /> : "Create Account"}
                    </button>

                    <p className="auth-switch">
                      Already have an account?{" "}
                      <button type="button" className="auth-link" onClick={() => setTab("login")}>
                        Sign in
                      </button>
                    </p>
                  </form>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Login;
